/**
 * Branding / `rechnerCustomConfig` boolean flags may arrive as real booleans
 * (SSR JSON) or as strings (`"true"` / `"false"`). Normalize both.
 */

/** True when a flag is explicitly false-like (boolean or string). */
export function isConfigFlagDisabled(raw: unknown): boolean {
  if (raw === undefined || raw === null || raw === "") return false;
  const v = String(raw).trim().toLowerCase();
  return v === "false" || v === "0" || v === "no" || v === "off";
}

/**
 * Default-on flags: only explicit false-like values disable.
 * Accepts boolean `false` and string `"false"` / `"0"` / `"no"` / `"off"`.
 */
export function isConfigFlagEnabled(
  raw: unknown,
  defaultWhenUnset = true,
): boolean {
  if (raw === undefined || raw === null || raw === "") {
    return defaultWhenUnset;
  }
  return !isConfigFlagDisabled(raw);
}

/**
 * Opt-in / default-off flags: only explicit true-like values enable.
 * Accepts boolean `true` and string `"true"` / `"1"` / `"yes"` / `"on"`.
 */
export function isConfigFlagTruthy(raw: unknown): boolean {
  if (raw === undefined || raw === null || raw === "") return false;
  const v = String(raw).trim().toLowerCase();
  return v === "true" || v === "1" || v === "yes" || v === "on";
}

/** Normalize a branding flag to the store string `"true"` | `"false"`. */
export function normalizeConfigFlagString(
  raw: unknown,
  defaultWhenUnset = true,
): "true" | "false" {
  return isConfigFlagEnabled(raw, defaultWhenUnset) ? "true" : "false";
}

/** Non-boolean branding config values (links, contact fields, etc.). */
export function configStringValue(raw: unknown): string | undefined {
  if (raw === undefined || raw === null || typeof raw === "boolean") {
    return undefined;
  }
  const value = String(raw).trim();
  return value === "" ? undefined : value;
}
